/**
 * Header of a motion column (W2-04a; DESIGN.md §4.7 "OPACITY [L0 · 1 +] {more-vertical} / now: 100% (Σ 100%)"),
 * portalled by the timeline panel into the host's header slot of the column:
 *
 *   row 1  the property name (click = focus the column: K, G, Ctrl+A act on it; double-click = open the curve editor),
 *          the Position X/Y {link} toggle, the unpin button of pinned columns, the column menu;
 *   row 2  layer chips (Full, summed properties), or status badges: "uses N layers" (Guided Σ view: offers Full),
 *          "in use", the line of a pinned column, Scroll speed mode badges ("baked on export", "1.7 speed");
 *   row 3  the live value at the playhead: the layer's value and Σ (summed properties with several layers); for
 *          Scroll speed the effective speed and "crosses in N s". Updated from refs ~15 times a second, never through
 *          React state per frame.
 * The focused column's header is highlighted (and says so to screen readers); its menu offers keyframes, adding a
 * property or a layer, the link, Σ, value range lock, raw events, pin / compare, the wide curve editor and Reset.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore, type ComponentType } from 'react';
import type { LineProp } from '../../core/model/types';
import { Units } from '../../core/model/units';
import { useT } from '../../i18n/useT';
import { Icon } from '../../ui/Icon';
import { Menu } from '../../ui/Menu';
import { Tooltip } from '../../ui/Tooltip';
import type { MenuItem } from '../../ui/types';
import { LayerChips } from './LayerChips';
import { isPinned, togglePin } from './CompareLines';
import { laneKeyOf, laneValueAt, playheadBeat } from './columnScene';
import { isLayered, layersOf, type ColumnSpec } from './columnSpec';
import { formatNumber, formatRgb, formatValue, propNames, quoteText, rpeTerm, subLabelKey } from './format';
import {
  addProperty,
  addablePropsOf,
  addLayer,
  isMuted,
  layerList,
  rawEventsJson,
  resetProperty,
  toggleLink,
} from './motionCommands';
import { levelOf, switchToFull } from './motionActions';
import type { MotionController } from './motionColumns';
import { m } from './motion.messages';
import s from './Motion.module.css';

/** Live value refresh period (ms): about 15 Hz. */
const LIVE_MS = 66;

/**
 * Re-renders on committed document changes (not on every live gesture frame), on the editor fields a header shows,
 * on settings and on session changes.
 */
function useMotionVersion(controller: MotionController): number {
  const counter = useRef(0);
  const subscribe = useCallback(
    (cb: () => void) => {
      const services = controller.session.services;
      const bump = (): void => {
        counter.current++;
        cb();
      };
      const offs = [
        controller.session.subscribe(bump),
        services.editor.subscribe((st, prev) => {
          if (
            st.focusedColumn !== prev.focusedColumn ||
            st.activeLayer !== prev.activeLayer ||
            st.activeLine !== prev.activeLine ||
            st.pinnedColumns !== prev.pinnedColumns ||
            st.filters.mutedLayers !== prev.filters.mutedLayers ||
            st.level !== prev.level ||
            st.selection !== prev.selection
          ) {
            bump();
          }
        }),
        services.settings.subscribe(bump),
        services.onChange(bump),
      ];
      const store = services.store;
      if (store !== null) {
        offs.push(
          store.subscribe((cs) => {
            if (cs.source !== 'gesture') bump();
          }),
        );
      }
      return () => {
        for (const off of offs) off();
      };
    },
    [controller],
  );
  return useSyncExternalStore(
    subscribe,
    () => counter.current,
    () => counter.current,
  );
}

function headerTitle(
  t: ReturnType<typeof useT>,
  spec: ColumnSpec,
): { readonly short: string; readonly tiny: string; readonly full: string } {
  if (spec.pair === 'position') return { short: t(m.shortPosition), tiny: t(m.tinyPosition), full: t(m.propPosition) };
  if (spec.pair === 'size') return { short: t(m.shortSize), tiny: t(m.tinySize), full: t(m.propSize) };
  const prop = spec.lanes[0]?.prop ?? 'moveX';
  const names = propNames(prop, spec);
  return { short: t(names.short), tiny: t(names.tiny), full: t(names.full) };
}

/**
 * The title in both lengths; a container query of the header shows the tiny one in narrow columns (UX-034: "X",
 * "Rot", "Op", "Spd" instead of "P...", "R...", "O...", "S...").
 */
function TitleText(props: { readonly title: { readonly short: string; readonly tiny: string } }) {
  return (
    <>
      <span className={s.titleWide}>{props.title.short}</span>
      <span className={s.titleTiny} aria-hidden="true">
        {props.title.tiny}
      </span>
    </>
  );
}

export function createColumnHeader(controller: MotionController): ComponentType<{ readonly instanceId: string }> {
  function MotionColumnHeader(props: { readonly instanceId: string }) {
    const { instanceId } = props;
    const version = useMotionVersion(controller);
    const t = useT();
    const nowRef = useRef<HTMLSpanElement>(null);
    const nowNarrowRef = useRef<HTMLSpanElement>(null);
    const sigmaRef = useRef<HTMLSpanElement>(null);
    const session = controller.session;
    const services = session.services;
    const spec = controller.specOf(instanceId);

    // Live values at the playhead, written into the spans directly.
    useEffect(() => {
      const sp = controller.specOf(instanceId);
      if (sp === null) return undefined;
      let lastNow = '';
      let lastNarrow = '';
      let lastTitle = '';
      let lastSigma = '';
      const tick = (): void => {
        const svc = controller.session.services;
        const engine = svc.engine;
        const lane = sp.lanes[0];
        if (engine === null || lane === undefined) return;
        let nowText: string;
        let narrowText: string;
        let sigmaText = '';
        try {
          const beat = playheadBeat(controller.session);
          const sec = engine.tempo.secAtBeatF64(beat);
          // The live readout translates like the rest of the header (the locale's catalogue).
          const tr = (k: string, p?: Readonly<Record<string, string | number>>): string => t(k, p);
          const parts: string[] = [];
          for (const l of sp.lanes) {
            if (l.prop === 'color' || l.prop === 'text') {
              const st = engine.lineState(sp.line, sec);
              parts.push(
                l.prop === 'text'
                  ? quoteText(tr, st.text ?? '')
                  : st.color === null
                    ? tr(m.valueNone)
                    : formatRgb(
                        tr,
                        st.color.map((c) => Math.round(c * 255)),
                      ),
              );
              continue;
            }
            parts.push(formatValue(tr, l.prop, laneValueAt(engine, sp, l, sec), sp));
          }
          const [a, b] = parts;
          nowText = b === undefined ? tr(m.headerNow, { value: a ?? '' }) : tr(m.headerNowPair, { a: a ?? '', b });
          // Narrow columns show the value alone (UX-034): the readout keeps at least the value readable.
          narrowText = b === undefined ? (a ?? '') : `${a ?? ''} | ${b}`;
          if (lane.prop === 'speed') {
            const v = laneValueAt(engine, sp, lane, sec);
            const cross = Units.crossScreenSec(v);
            sigmaText = Number.isFinite(cross)
              ? tr(m.headerCrosses, { sec: formatNumber(cross, 2) })
              : tr(m.headerCrossesNever);
          } else if (isLayered(lane.prop) && !sp.sumView && sp.layersInUse.length > 1 && sp.lanes.length === 1) {
            sigmaText = tr(m.headerSigma, {
              value: formatValue(tr, lane.prop, engine.channelValue(sp.line, lane.prop, sec, 'sum'), sp),
            });
          }
        } catch {
          return;
        }
        if (nowText !== lastNow && nowRef.current !== null) {
          nowRef.current.textContent = nowText;
          lastNow = nowText;
        }
        if (narrowText !== lastNarrow && nowNarrowRef.current !== null) {
          nowNarrowRef.current.textContent = narrowText;
          lastNarrow = narrowText;
        }
        // The whole readout stays available as a tooltip in narrow columns.
        const titleText = sigmaText === '' ? nowText : `${nowText} · ${sigmaText}`;
        if (titleText !== lastTitle && nowNarrowRef.current !== null) {
          nowNarrowRef.current.title = titleText;
          lastTitle = titleText;
        }
        if (sigmaText !== lastSigma && sigmaRef.current !== null) {
          sigmaRef.current.textContent = sigmaText;
          lastSigma = sigmaText;
        }
      };
      tick();
      const timer = setInterval(tick, LIVE_MS);
      return () => {
        clearInterval(timer);
      };
    }, [instanceId, version, t]);

    if (spec === null) return null;
    const store = services.store;
    const line = store?.doc.lines.get(spec.line);
    if (store === null || line === undefined) return null;
    const editor = services.editor.getState();
    const focus = editor.focusedColumn;
    const firstLane = spec.lanes[0];
    if (firstLane === undefined) return null;
    const prop: LineProp = firstLane.prop;
    // The same property of the same line on another layer (a pinned layer column) is another column.
    const isFocusedLane = (l: ColumnSpec['lanes'][number]): boolean =>
      focus !== null &&
      focus.line === spec.line &&
      l.prop === focus.prop &&
      (spec.sumView ? focus.layer === 0 : l.layer === focus.layer);
    const focused = spec.lanes.some(isFocusedLane);
    const title = headerTitle(t, spec);
    const level = levelOf(services);
    const compact = controller.compact(instanceId);
    const layered = isLayered(prop);
    const focusLane = (laneIndex: number): void => {
      const l = spec.lanes[laneIndex];
      if (l === undefined) return;
      services.editor.getState().setFocusedColumn({ line: spec.line, prop: l.prop, layer: spec.sumView ? 0 : l.layer });
    };
    const focusedLane = spec.lanes.find(isFocusedLane);
    const focusedLaneRef = {
      line: spec.line,
      prop: focusedLane?.prop ?? prop,
      layer: spec.sumView ? 0 : (focusedLane ?? firstLane).layer,
    };
    /**
     * Any press on the header (padding, readout, a gap between controls) is a click on the column: it focuses the
     * column's current lane so K and the hint bar agree with the highlighted header. The title button and the layer
     * chips then narrow it to their own lane on their click. (A press only on the 2 px border + 2 px padding used
     * to leave `focusedColumn` null — "Click a motion column first" after clicking a motion column.)
     */
    const focusFromHeader = (): void => {
      const idx = focusedLane === undefined ? 0 : spec.lanes.indexOf(focusedLane);
      focusLane(idx < 0 ? 0 : idx);
    };
    const runCmd = (id: string, args?: Parameters<typeof services.registry.runAny>[1]): void => {
      focusLane(
        Math.max(
          0,
          spec.lanes.findIndex((l) => l.prop === focusedLaneRef.prop),
        ),
      );
      void services.registry.runAny(id, args, 'menu');
    };

    if (compact) {
      return (
        <div
          className={s.header}
          data-focused={focused}
          data-pinned={spec.pinned}
          data-tl-header-motion=""
          onPointerDown={focusFromHeader}
        >
          <button
            type="button"
            className={s.title}
            title={t(m.headerExpand)}
            aria-label={t(m.headerCompactLabel, { prop: title.full })}
            onClick={() => {
              controller.toggleCompactExpanded(instanceId);
            }}
          >
            {title.tiny}
          </button>
        </div>
      );
    }

    const linked = line.editor.xyLinked;
    const lk = laneKeyOf(spec.line, prop, firstLane.layer);
    const speedMode = store.doc.pkg.speedMode;
    const addable = addablePropsOf(store, spec.line, level);
    const pinnedNow = isPinned(services, focusedLaneRef);

    const items: MenuItem[] = [
      {
        kind: 'action',
        label: t(m.cmdAddKey),
        icon: 'DiamondPlus',
        onSelect: () => {
          runCmd('keys.addAtPlayhead');
        },
      },
      {
        kind: 'action',
        label: t(m.cmdSelectColumn),
        icon: 'Rows3',
        onSelect: () => {
          runCmd('keys.selectColumn');
        },
      },
      { kind: 'separator' },
    ];
    if (addable.length > 0) {
      items.push({
        kind: 'submenu',
        label: t(m.cmdAddProperty),
        items: addable.map((p): MenuItem => ({
          kind: 'action',
          label: t(propNames(p, spec).full),
          onSelect: () => {
            void addProperty(services, spec.line, p);
          },
        })),
      });
    }
    if (layered && level === 'full' && !spec.pinned) {
      items.push({
        kind: 'action',
        label: t(m.cmdLayerAdd),
        icon: 'Layers',
        onSelect: () => addLayer(services, spec.line, prop),
      });
    }
    if (prop === 'moveX' || prop === 'moveY') {
      items.push({
        kind: 'action',
        label: t(linked ? m.headerLinkLabelOn : m.headerLinkLabelOff),
        icon: linked ? 'Link' : 'Unlink',
        onSelect: () => toggleLink(services, spec.line),
      });
    }
    if (layered && level === 'full' && spec.layersInUse.length > 1) {
      const hidden = session.sigmaHidden.has(`${String(spec.line)}:${prop}`);
      items.push({
        kind: 'action',
        label: t(m.cmdToggleSigma),
        icon: hidden ? 'EyeOff' : 'Sigma',
        onSelect: () => {
          runCmd('motion.toggleSigma');
        },
      });
    }
    if (prop !== 'color' && prop !== 'text') {
      const locked = session.lockedRanges.has(lk);
      items.push({
        kind: 'action',
        label: t(m.cmdLockRange),
        icon: locked ? 'Lock' : 'LockOpen',
        onSelect: () => {
          runCmd('motion.lockRange');
        },
      });
    }
    if (level === 'full') {
      items.push({
        kind: 'action',
        label: t(m.cmdShowRaw),
        icon: 'Braces',
        onSelect: () => {
          runCmd('motion.toggleRaw');
        },
      });
      items.push({
        kind: 'action',
        label: t(m.cmdCopyRaw),
        icon: 'Copy',
        disabledReason: rawEventsJson(services, focusedLaneRef) === null ? { key: m.rawCopyFailed } : null,
        onSelect: () => {
          runCmd('motion.copyRaw');
        },
      });
    }
    items.push({ kind: 'separator' });
    items.push({
      kind: 'action',
      label: t(pinnedNow ? m.cmdUnpinColumn : m.cmdPinColumn),
      icon: pinnedNow ? 'PinOff' : 'Pin',
      onSelect: () => {
        togglePin(services, focusedLaneRef);
      },
    });
    if (level === 'full')
      items.push({
        kind: 'action',
        label: t(m.cmdCompare),
        icon: 'GitCompareArrows',
        onSelect: () => {
          runCmd('columns.compare', { prop });
        },
      });
    if (prop !== 'color' && prop !== 'text') {
      items.push({
        kind: 'action',
        label: t(m.headerExpand),
        icon: 'Maximize2',
        onSelect: () => {
          focusLane(0);
          void services.registry.run('curve.expand', { column: instanceId }, 'menu');
        },
      });
    }
    items.push({ kind: 'separator' });
    items.push({
      kind: 'action',
      label: t(m.cmdResetProperty),
      icon: 'Undo2',
      onSelect: () => {
        focusLane(0);
        void resetProperty(services, focusedLaneRef);
      },
    });

    const usesLayers = spec.sumView ? Math.max(2, spec.layersInUse.length) : 0;
    const pinnedLine = spec.pinned ? line.name : null;
    const crossLabel = t(rpeTerm(prop));
    const subA = spec.lanes[0]?.sub ?? null;
    const subB = spec.lanes[1]?.sub ?? null;
    const pairSub =
      subA === null || subB === null ? null : t(m.subPair, { a: t(subLabelKey(subA)), b: t(subLabelKey(subB)) });

    return (
      <div
        className={s.header}
        data-focused={focused}
        data-pinned={spec.pinned}
        data-help-id="motion-columns"
        role="group"
        aria-label={headerAria(t, title.full, pinnedLine, focused)}
        onPointerDown={focusFromHeader}
      >
        <div className={s.row}>
          <Tooltip
            content={t(m.headerTooltip, {
              prop: title.full,
              rpe: crossLabel,
              hint: t(focused ? m.headerFocused : m.headerFocusHint),
            })}
          >
            <button
              type="button"
              className={s.title}
              aria-label={title.full}
              aria-pressed={focused}
              onClick={() => {
                focusLane(0);
              }}
              onDoubleClick={() => {
                if (prop === 'color' || prop === 'text') return;
                focusLane(0);
                // The curve editor (W2-04b) owns opening itself (checks, focus, relayout).
                void services.registry.run('curve.expand', { column: instanceId }, 'button');
              }}
            >
              <TitleText title={title} />
              {pairSub === null ? null : <span className={s.sub}> {pairSub}</span>}
            </button>
          </Tooltip>
          {spec.pair === 'position' || prop === 'moveX' || prop === 'moveY' ? (
            <button
              type="button"
              className={`${s.iconBtn} ${s.linkBtn}`}
              aria-pressed={linked}
              aria-label={t(linked ? m.headerLinkLabelOn : m.headerLinkLabelOff)}
              title={t(linked ? m.headerLinkOn : m.headerLinkOff)}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                toggleLink(services, spec.line);
              }}
            >
              <Icon name={linked ? 'Link' : 'Unlink'} size={11} />
            </button>
          ) : null}
          {spec.pinned ? (
            <button
              type="button"
              className={s.iconBtn}
              aria-label={t(m.headerUnpin)}
              title={t(m.headerUnpin)}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                togglePin(services, { line: spec.line, prop, layer: firstLane.layer });
              }}
            >
              <Icon name="PinOff" size={11} />
            </button>
          ) : null}
          <Menu
            trigger={
              <button
                type="button"
                className={s.iconBtn}
                aria-label={t(m.headerMenu)}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                <Icon name="EllipsisVertical" size={12} />
              </button>
            }
            items={items}
            align="end"
          />
        </div>
        <div className={s.chipRow}>
          {layered && level === 'full' && !spec.pinned && spec.lanes.length === 1 ? (
            <LayerChips services={services} line={spec.line} prop={prop} propLabel={title.full} version={version} />
          ) : null}
          {usesLayers > 0 ? (
            <button
              type="button"
              className={s.badge}
              data-tone="inUse"
              title={t(m.headerUsesLayersHint, { n: usesLayers })}
              aria-label={t(m.headerUsesLayers, { n: usesLayers })}
              onClick={() => {
                switchToFull(services);
              }}
            >
              <Icon name="Layers" size={10} />
              <span className={s.wideOnly}>{t(m.headerUsesLayers, { n: usesLayers })}</span>
              <span className={s.narrowOnly}>{t(m.headerUsesLayersShort, { n: usesLayers })}</span>
            </button>
          ) : null}
          {spec.inUseBadge ? (
            <span className={s.badge} data-tone="inUse" title={t(m.headerInUseHint)}>
              {t(m.headerInUse)}
            </span>
          ) : null}
          {layered && layerList(store, spec.line, prop).some((l) => isMuted(services, spec.line, prop, l)) ? (
            <span className={s.badge} data-tone="warning" title={t(m.layerMutedNote)}>
              <Icon name="VolumeX" size={10} />
              {t(m.headerPreviewOnly)}
            </span>
          ) : null}
          {pinnedLine !== null ? (
            <span className={s.badge} title={t(m.headerPinnedFrom, { line: pinnedLine })}>
              <Icon name="Pin" size={10} />
              {pinnedLine}
            </span>
          ) : null}
          {prop === 'speed' && speedMode === 'legacy' && hasEasedSpeed(services, spec) ? (
            <span
              className={s.badge}
              data-tone="baked"
              title={t(m.headerSpeedBakedHint)}
              aria-label={t(m.headerSpeedBaked)}
            >
              <Icon name="Scissors" size={10} />
              <span className={s.wideOnly}>{t(m.headerSpeedBaked)}</span>
            </span>
          ) : null}
          {prop === 'speed' && speedMode === 'rpe170' ? (
            <span
              className={s.badge}
              data-tone="info"
              title={t(store.doc.pkg.rpeVersion >= 170 ? m.headerSpeed170Hint : m.headerSpeed170LegacyHint)}
              aria-label={t(store.doc.pkg.rpeVersion >= 170 ? m.headerSpeed170 : m.headerSpeed170Legacy)}
            >
              <Icon name="Gauge" size={10} />
              <span className={s.wideOnly}>
                {t(store.doc.pkg.rpeVersion >= 170 ? m.headerSpeed170 : m.headerSpeed170Legacy)}
              </span>
            </span>
          ) : null}
        </div>
        <div className={s.now} aria-live="off">
          <span ref={nowRef} className={s.nowWide} />
          <span ref={nowNarrowRef} className={s.nowNarrow} />
          <span ref={sigmaRef} className={s.sigma} data-kind={prop === 'speed' ? 'crosses' : 'sigma'} />
        </div>
      </div>
    );
  }
  return MotionColumnHeader;
}

/** Accessible name of the header group: the property, the line of a pinned column, the focused state. */
function headerAria(t: ReturnType<typeof useT>, prop: string, pinnedLine: string | null, focused: boolean): string {
  if (pinnedLine !== null)
    return focused
      ? t(m.headerAriaPinnedFocused, { prop, line: pinnedLine })
      : t(m.headerAriaPinned, { prop, line: pinnedLine });
  return focused ? t(m.headerAriaFocused, { prop }) : prop;
}

function hasEasedSpeed(services: MotionController['session']['services'], spec: ColumnSpec): boolean {
  const store = services.store;
  if (store === null) return false;
  for (const layer of layersOf(store.doc, store.idx, spec.line, 'speed')) {
    const track = store.idx.track(spec.line, 'speed', layer);
    if (track === undefined) continue;
    for (const id of store.idx.keysByTrack(track)) {
      const e = store.doc.keys.get(id)?.ease;
      if (
        e !== undefined &&
        e.kind !== 'hold' &&
        (e.kind === 'bezier' || e.id !== 1) &&
        store.idx.nextKey(id) !== undefined
      )
        return true;
    }
  }
  return false;
}
